import { ReactNode, useEffect, useMemo } from 'react';
import seededSampleSize from '../lib/seededSampleSize';
import getSeed from '../lib/getSeed';

// code point of A
const startCodePoint = 65;

// gets the letter of the variant A, B, C, ...
const getVariantLetter = (
  variants: ReactNode[],
  variant: ReactNode,
) => String.fromCodePoint(startCodePoint + variants.indexOf(variant));

const getPersistedSeed = (testName: string): number => {
  try {
    const storageKey = `abtest-seed-${testName}`;
    const storedSeed = localStorage.getItem(storageKey);

    if (storedSeed) return Number(storedSeed);

    const newSeed = getSeed();
    localStorage.setItem(storageKey, newSeed.toString());
    return newSeed;
  } catch {
    return getSeed();
  }
};

const ABTest = <T extends ReactNode | string>({
  name,
  variants,
  properties,
  onVariantChosen,
  getConversionTracker,
  getVariantName = getVariantLetter,
}: {
  name: string;
  variants: T[];
  properties?: Record<string, unknown>;
  onVariantChosen?: (variantLetter: string) => void;
  getConversionTracker?: (
    trackConversion: (
      properties?: Record<string, unknown>
    ) => void
  ) => void;
  getVariantName?: (variants: T[], variant: T) => string;
}) => {
  const [variant] = useMemo(() => {
    const seed = getPersistedSeed(name);
    return seededSampleSize(variants, 1, seed);
  }, [name, variants]);

  useEffect(() => {
    window.plausible('ABTest', {
      props: {
        ...properties,
        name,
        variant: getVariantName(variants, variant),
      },
    });
  }, [name, properties, getVariantName, variant, variants]);

  useEffect(() => {
    onVariantChosen?.(getVariantName(variants, variant));
  }, [onVariantChosen, getVariantName, variant, variants]);

  useEffect(() => {
    getConversionTracker?.((conversionProperties?: Record<string, unknown>) => {
      window.plausible('ABTestConversion', {
        props: {
          ...properties,
          ...conversionProperties,
          name,
          variant: getVariantName(variants, variant),
        },
      });
    });
  }, [
    getConversionTracker,
    name,
    getVariantName,
    variant,
    variants,
    properties,
  ]);

  return variant;
};

export default ABTest;
